import { useState } from 'react'
import styled from 'styled-components'

import { Redacted } from '@blade/std-lib'
import { Button, useHapticFeedback } from '@blade/ui-kit'
import { useClipboard } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { SegmentControl } from '@root/shared/ui-kit'

import type { ExportedWallet } from '@root/features/secure-storage/secure-storage-service'
import type { ClassNameProps } from '@root/shared/react'

type SecretType = 'phrase' | 'privateKey'

type Props = {
  keys: Pick<ExportedWallet, 'mnemonic' | 'privateKey'>
  onDone?: () => void
} & ClassNameProps

export const ExportWalletKeys: React.FC<Props> = ({ keys, className }) => {
  const mnemonic = keys.mnemonic ? Redacted.value(keys.mnemonic) : undefined
  const privateKey = Redacted.value(keys.privateKey)

  const [secretType, setSecretType] = useState<SecretType>(
    mnemonic ? 'phrase' : 'privateKey',
  )

  const haptics = useHapticFeedback()
  const { copyToClipboard } = useClipboard()
  const { t } = useTranslation()

  const handleCopy = () => {
    const subject =
      secretType === 'phrase'
        ? t('wallet.secret_phrase')
        : t('wallet.private_key')
    const text = secretType === 'phrase' ? mnemonic : privateKey
    const snackbarText = t('wallet.export_wallet.copied-note', { subject })

    if (!text) {
      return
    }

    haptics.impactOccurred('light')
    copyToClipboard(text, {
      snackbarText: snackbarText,
      showSnackbar: true,
    })
  }

  return (
    <Root className={className}>
      {mnemonic ? (
        <TabsContainer>
          <SegmentControl value={secretType} onChange={setSecretType}>
            <SegmentControl.Item value={'phrase' satisfies SecretType}>
              {t('wallet.phrase')}
            </SegmentControl.Item>
            <SegmentControl.Item value={'privateKey' satisfies SecretType}>
              {t('wallet.private_key')}
            </SegmentControl.Item>
          </SegmentControl>
        </TabsContainer>
      ) : undefined}

      <Label>
        {secretType === 'phrase'
          ? t('wallet.secret_phrase')
          : t('wallet.private_key')}
      </Label>

      <Area>
        <AreaInternal>
          {secretType === 'phrase' ? (
            <MnemonicPhrase>
              {mnemonic ? (
                mnemonic
                  .split(' ')
                  .map((it) => <MnemonicWord key={it}>{it}</MnemonicWord>)
              ) : (
                <MnemonicWord>-</MnemonicWord>
              )}
            </MnemonicPhrase>
          ) : (
            <PrivateKey>{privateKey}</PrivateKey>
          )}
        </AreaInternal>
        <CopyBtn>
          <Button
            radius="rounded"
            color="primary"
            variant="ghost"
            fullWidth
            size="xs"
            onClick={handleCopy}
          >
            {t('common.copy')}
          </Button>
        </CopyBtn>
      </Area>
    </Root>
  )
}

const Root = styled.div``

const Area = styled.div`
  border: 1px solid var(--backgroundA-2);
  background: var(--appearanceA-7);

  overflow: hidden;
  position: relative;

  height: 210px;
  border-radius: 12px;
`

const AreaInternal = styled.div`
  color: var(--text-11);
  font-size: 11px;

  padding: 16px;
  width: 100%;
  height: 100%;
  overflow: auto;
`

const PrivateKey = styled.div`
  word-break: break-all;
`

const MnemonicWord = styled.div`
  background: var(--backgroundA-2);
  color: var(--text-10);

  padding-inline: 8px;
  padding-block: 8px;
  border-radius: 6px;

  text-align: center;

  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`

const MnemonicPhrase = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const Label = styled.div`
  color: var(--text-11);

  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  margin-bottom: 8px;
`

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-block: 0 8px;
`

const CopyBtn = styled.div`
  position: absolute;
  bottom: 6px;
  right: 6px;
`
